// 45-Day Aptitude Path — full curriculum view + per-day status grid.
// Day cells:
//   done     ✓ — student has completed this day
//   today    ★ — next un-completed day (where /today resolves)
//   upcoming — locked? no. Visible but not actionable from this page;
//              the daily push gates progression to one day at a time.
// Tap a day → preview modal with strategy + 5 Q outlines + "Start" button
//             ("Start" available on today's day only).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  PathDayDetailResponse,
  PathOverviewResponse,
  aptitudeDrillAPI,
} from '../services/aptitudeDrillAPI';
import { track } from '../services/api';
import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import { displayTopic } from '../config/topicDisplayNames';
import { useTheme } from '../contexts/ThemeContext';

// Theme-aware palette. Light values are byte-for-byte the originals.
const makeColors = (dark: boolean) => ({
  primary: dark ? '#86c232' : '#4a7c10',
  primaryDark: dark ? '#6fa029' : '#3a6209',
  border: dark ? '#262c35' : '#e2e8f0',
  bg: dark ? '#0f1216' : '#f8fafc',
  textPrimary: dark ? '#e6e8eb' : '#0f172a',
  textMuted: dark ? '#aab1ba' : '#64748b',
  done: dark ? '#86c232' : '#16a34a',
  doneBg: dark ? 'rgba(134,194,50,0.12)' : '#dcfce7',
  today: dark ? '#f5c563' : '#f59e0b',
  todayBg: dark ? 'rgba(245,176,65,0.12)' : '#fef3c7',
  upcoming: dark ? '#2d333b' : '#cbd5e1',
  upcomingBg: dark ? '#12161c' : '#f8fafc',
});

const AptitudePath: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  const [overview, setOverview] = useState<PathOverviewResponse | null>(null);
  const [selectedDay, setSelectedDay] = useState<PathDayDetailResponse | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await aptitudeDrillAPI.path();
        if (!cancelled) {
          setOverview(data);
          track('aptitude_path.opened', {
            current_day: data.current_day,
            completed_days: data.completed_days,
            total_days: data.total_days,
          });
        }
      } catch (e: any) {
        if (e?.response?.status === 401) {
          navigate('/login');
          return;
        }
        setError(e?.response?.data?.detail || 'Could not load the path.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const openDay = useCallback(async (day: number) => {
    setModalLoading(true);
    setSelectedDay(null);
    try {
      const data = await aptitudeDrillAPI.pathDay(day);
      setSelectedDay(data);
      track('aptitude_path.day_previewed', {
        day_index: day,
        status: data.status,
        topic: data.topic,
      });
    } catch (e) {
      // Silent — modal just won't open
    } finally {
      setModalLoading(false);
    }
  }, []);

  const startToday = useCallback(() => {
    track('aptitude_path.day_started', {
      day_index: overview?.current_day,
    });
    navigate('/aptitude-drill/today?from=path');
  }, [navigate, overview]);

  if (error) {
    return (
      <Shell onBack={() => navigate('/micro-learning')}>
        <div style={{ ...cardStyle(dark), color: dark ? '#ff6b6b' : '#dc2626' }}>{error}</div>
      </Shell>
    );
  }

  if (!overview) {
    return (
      <Shell onBack={() => navigate('/micro-learning')}>
        <div style={{ ...cardStyle(dark), color: COLORS.textMuted }}>Loading the 45-day path…</div>
      </Shell>
    );
  }

  return (
    <Shell onBack={() => navigate('/micro-learning')}>
      <div style={cardStyle(dark)}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 4,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 22, color: COLORS.textPrimary }}>
            45-Day Aptitude Path
          </h2>
          <button
            onClick={() => {
              track('aptitude_history.entry_tapped', { from: 'aptitude_path' });
              navigate('/aptitude-drill/history');
            }}
            style={{
              background: 'transparent',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 999,
              padding: '6px 12px',
              fontSize: 13,
              color: COLORS.textPrimary,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            📚 Past drills
          </button>
        </div>
        <p style={{ marginTop: 6, marginBottom: 16, color: COLORS.textMuted, fontSize: 14 }}>
          One topic per day, 5 questions, strategy + instant explanations. Ordered by ESLCE
          frequency. Day {overview.current_day} of {overview.total_days} —{' '}
          {overview.completed_days} done.
        </p>

        <div style={progressTrackStyle(dark)}>
          <div
            style={{
              ...progressBarStyle(dark),
              width: `${(overview.completed_days / overview.total_days) * 100}%`,
            }}
          />
        </div>

        <button
          onClick={startToday}
          style={primaryBtnStyle(dark)}
          aria-label={`Start day ${overview.current_day}`}
        >
          Start day {overview.current_day} →
        </button>

        <div style={gridStyle}>
          {overview.days.map(d => {
            const palettes = {
              done: { bg: COLORS.doneBg, border: COLORS.done, color: COLORS.done },
              today: { bg: COLORS.todayBg, border: COLORS.today, color: dark ? '#f5c563' : '#92400e' },
              upcoming: {
                bg: COLORS.upcomingBg,
                border: COLORS.upcoming,
                color: COLORS.textMuted,
              },
            };
            const p = palettes[d.status];
            const icon = d.status === 'done' ? '✓' : d.status === 'today' ? '★' : d.day_index;
            return (
              <button
                key={d.day_index}
                onClick={() => openDay(d.day_index)}
                style={{
                  ...dayCellStyle,
                  background: p.bg,
                  border: `1.5px solid ${p.border}`,
                  color: p.color,
                }}
                title={`${d.strategy_title}`}
              >
                <div style={{ fontWeight: 700, fontSize: 18 }}>{icon}</div>
                <div style={{ fontSize: 10, marginTop: 2, lineHeight: 1.1 }}>
                  {displayTopic(d.topic) || d.topic}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {(modalLoading || selectedDay) && (
        <Modal onClose={() => setSelectedDay(null)} loading={modalLoading} day={selectedDay}>
          {selectedDay && (
            <DayPreview day={selectedDay} canStart={selectedDay.status === 'today'} onStart={startToday} />
          )}
        </Modal>
      )}
    </Shell>
  );
};

// ----- subcomponents -----

const Shell: React.FC<{ children: React.ReactNode; onBack: () => void }> = ({ children, onBack }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  return (
  <div style={{ minHeight: '100vh', background: COLORS.bg }}>
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        background: dark ? '#171b21' : 'white',
        borderBottom: `1px solid ${COLORS.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <button
        onClick={onBack}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '8px 12px',
          cursor: 'pointer',
          color: dark ? '#aab1ba' : '#475569',
        }}
      >
        ← Back
      </button>
      <h1 style={{ margin: 0, marginLeft: 8, fontSize: '1.05rem', flex: 1 }}>Aptitude Path</h1>
    </header>
    <main style={{ maxWidth: 720, margin: '0 auto', padding: '12px 16px 80px' }}>{children}</main>
  </div>
  );
};

const Modal: React.FC<{
  onClose: () => void;
  loading: boolean;
  day: PathDayDetailResponse | null;
  children?: React.ReactNode;
}> = ({ onClose, loading, day, children }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  return (
  <div
    onClick={onClose}
    style={{
      position: 'fixed',
      inset: 0,
      background: dark ? 'rgba(0,0,0,0.65)' : 'rgba(15,23,42,0.55)',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      zIndex: 50,
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: dark ? '#171b21' : 'white',
        borderRadius: '16px 16px 0 0',
        padding: 20,
        maxWidth: 720,
        width: '100%',
        maxHeight: '85vh',
        overflowY: 'auto',
      }}
    >
      {loading && <div style={{ color: COLORS.textMuted }}>Loading day…</div>}
      {!loading && !day && <div style={{ color: COLORS.textMuted }}>Couldn't load this day.</div>}
      {children}
      <button
        onClick={onClose}
        style={{
          marginTop: 12,
          width: '100%',
          padding: '10px 14px',
          background: 'transparent',
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
          cursor: 'pointer',
          color: COLORS.textPrimary,
        }}
      >
        Close
      </button>
    </div>
  </div>
  );
};

const DayPreview: React.FC<{
  day: PathDayDetailResponse;
  canStart: boolean;
  onStart: () => void;
}> = ({ day, canStart, onStart }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  return (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span
        style={{
          padding: '4px 10px',
          background:
            day.status === 'done'
              ? COLORS.doneBg
              : day.status === 'today'
                ? COLORS.todayBg
                : dark ? '#12161c' : '#f1f5f9',
          color:
            day.status === 'done'
              ? COLORS.done
              : day.status === 'today'
                ? dark ? '#f5c563' : '#92400e'
                : COLORS.textMuted,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {day.status === 'done' ? 'Done' : day.status === 'today' ? 'Today' : 'Upcoming'}
      </span>
      <span style={{ color: COLORS.textMuted, fontSize: 13 }}>
        {displayTopic(day.topic) || day.topic}
      </span>
    </div>
    <h3 style={{ margin: '12px 0 4px', fontSize: 18, color: COLORS.textPrimary }}>
      {day.strategy_title}
    </h3>
    <div style={{ marginTop: 12, fontSize: 14, color: COLORS.textPrimary, lineHeight: 1.55 }}>
      <MarkdownRenderer>{day.strategy_summary_md}</MarkdownRenderer>
    </div>

    <div style={{ marginTop: 16, fontSize: 13, fontWeight: 600, color: COLORS.textMuted }}>
      {day.questions.length} questions today
    </div>

    {canStart && (
      <button onClick={onStart} style={{ ...primaryBtnStyle(dark), marginTop: 12 }}>
        Start day {day.day_index} →
      </button>
    )}
    {!canStart && day.status === 'upcoming' && (
      <div style={{ marginTop: 12, padding: 12, background: dark ? '#12161c' : '#f1f5f9', borderRadius: 8, fontSize: 13, color: COLORS.textMuted }}>
        Available once you complete the days before it.
      </div>
    )}
    {!canStart && day.status === 'done' && (
      <div style={{ marginTop: 12, padding: 12, background: COLORS.doneBg, borderRadius: 8, fontSize: 13, color: COLORS.done }}>
        You've finished this day. Future review will be available after launch.
      </div>
    )}
  </div>
  );
};

// ----- styles -----

const cardStyle = (dark: boolean): React.CSSProperties => ({
  background: dark ? '#171b21' : 'white',
  border: `1px solid ${makeColors(dark).border}`,
  borderRadius: 14,
  padding: 16,
  boxShadow: dark ? '0 1px 0 rgba(0,0,0,0.4)' : '0 1px 0 rgba(0,0,0,0.02)',
});

const progressTrackStyle = (dark: boolean): React.CSSProperties => ({
  height: 8,
  background: dark ? '#262c35' : '#e2e8f0',
  borderRadius: 999,
  overflow: 'hidden',
  marginBottom: 12,
});

const progressBarStyle = (dark: boolean): React.CSSProperties => ({
  height: '100%',
  background: makeColors(dark).primary,
  transition: 'width 300ms ease',
});

const primaryBtnStyle = (dark: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '12px 16px',
  background: makeColors(dark).primary,
  color: dark ? '#0f1216' : 'white',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
});

const gridStyle: React.CSSProperties = {
  marginTop: 16,
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  gap: 8,
};

const dayCellStyle: React.CSSProperties = {
  aspectRatio: '1 / 1',
  borderRadius: 10,
  padding: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  textAlign: 'center',
  fontFamily: 'inherit',
};

export default AptitudePath;
